import classNames from "classnames";
import React, { FC, useState } from "react";
import { LuSparkles } from "react-icons/lu";

import { useUpdateAiSettingsMutation } from "../../slices/AiSlice";
import { aiErrorMessage } from "./ai.utils";
import { useAiStatus } from "./useAiStatus";

export const AI_SETTINGS_TEXT =
  "Lets your team use AI to draft and polish emails and notes, and summarize work items. AI only writes drafts; nothing is sent without someone clicking Send. Your data is not used to train AI.";

/** Settings > Firm Details: the firm-wide "AI features" on/off switch. */
export const AiSettingsCard: FC = () => {
  const { status, orgId, isLoading, isError } = useAiStatus();
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateAiSettingsMutation();
  const [error, setError] = useState<string | null>(null);

  const configured = !!status?.configured && !isError;
  const enabled = configured && !!status?.enabled;
  const canManage = !!status?.canManage;

  const toggle = async () => {
    if (!canManage || isSaving) return;
    setError(null);
    try {
      await updateSettings({ orgId, enabled: !enabled }).unwrap();
    } catch (caught) {
      setError(aiErrorMessage(caught));
    }
  };

  return (
    <div
      className={
        "mt-6 flex items-start justify-between gap-6 rounded-xl border border-[#E4E9F2] bg-white p-6 font-Manrope"
      }>
      <div className={"flex max-w-[640px] gap-3"}>
        <span
          className={
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F0EDFF] text-[#6B4EFF]"
          }>
          <LuSparkles size={17} />
        </span>
        <div>
          <div className={"text-[15px] font-bold text-[#1F2433]"}>
            AI features
          </div>
          <div className={"mt-1 text-[13.5px] leading-[1.5] text-[#5B6678]"}>
            {AI_SETTINGS_TEXT}
          </div>
          {isLoading ? null : !configured ? (
            <div className={"mt-2 text-[13px] font-semibold text-[#8A90A2]"}>
              AI isn&apos;t set up on this server yet.
            </div>
          ) : (
            <>
              {enabled && status && status.limitPerDay > 0 && (
                <div className={"mt-2 text-[12.5px] text-[#8A90A2]"}>
                  Used today: {status.usedToday} of {status.limitPerDay}
                </div>
              )}
              {!canManage && (
                <div className={"mt-2 text-[12.5px] text-[#8A90A2]"}>
                  Only admins can change this setting.
                </div>
              )}
            </>
          )}
          {error && (
            <div
              role={"alert"}
              className={"mt-2 text-[12.5px] font-semibold text-[#B4262C]"}>
              {error}
            </div>
          )}
        </div>
      </div>
      {configured && (
        <label
          className={classNames(
            "flex shrink-0 items-center gap-2.5",
            canManage ? "cursor-pointer" : "cursor-not-allowed",
          )}>
          <span
            className={classNames(
              "text-[13px] font-semibold",
              enabled ? "text-[#5B3FE6]" : "text-[#8A90A2]",
            )}>
            {enabled ? "On" : "Off"}
          </span>
          <button
            type={"button"}
            role={"switch"}
            aria-checked={enabled}
            aria-label={"AI features"}
            disabled={!canManage || isSaving}
            onClick={() => void toggle()}
            className={classNames(
              "relative h-6 w-11 rounded-full transition-colors disabled:cursor-not-allowed",
              enabled ? "bg-[#6B4EFF]" : "bg-[#D5D9E2]",
              !canManage && "opacity-60",
              isSaving && "opacity-70",
            )}>
            <span
              className={classNames(
                "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                enabled ? "left-[22px]" : "left-0.5",
              )}
            />
          </button>
        </label>
      )}
    </div>
  );
};
